import {
  Stack,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  SxProps,
  TableSortLabel,
  TableBody,
  TablePagination,
  Pagination,
  Autocomplete,
  TextField,
  Typography,
} from "@mui/material";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
//import OtherKeywordST from "./OtherKeywordST";
import { useCallback, useEffect, useMemo, useState } from "react";
//import SaveChangesST from "./SaveChangesST";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { v4 as uuidv4 } from "uuid";
import { Theme } from "@emotion/react";
import dayjs from "dayjs";
import {
  formatNumber,
  formatTwoDecimal,
} from "../../../../../../../../../utils/helpers";
import {
  setDefinitionModalSourceForUnclassifiedST,
  setIsDefinitionModalOpenForUnclassifiedST,
  setIsOpenOtherKeywordDialogSTOUnclassified,
  setOtherKeywordRelatedToSearchTerm,
  setOtherKeywordsForUnclassifiedST,
  setSelectedDefinitionsForUnclassifiedST,
  setSelectedRowsForUnclassifiedST,
} from "../../../../../../../../../features/searchTermTabSlice";
import STUnclassifiedFilterCard from "./STUnclassifiedFilterCard";
import { getUniqueSearchTermsWithDuplicates } from "../../../../../utilsSearchTerm/helperST";
import { setDisplayErrorSnackBar } from "../../../../../../../../../features/userSlice";

const tableHeaders = [
  { id: 1, KEY: "search_term", label: "Search Term", sortable: true },
  { id: 2, KEY: "search_term_match_type", label: "Match Type", sortable: true },
  { id: 3, KEY: "keyword_name", label: "Keyword Name", sortable: true },
  {
    id: 4,
    KEY: "keyword_matchtype",
    label: "Keyword Match Type",
    sortable: true,
  },
  { id: 5, KEY: "adgroup_name", label: "Adgroup Name", sortable: true },
  { id: 6, KEY: "campaign_name", label: "Campaign Name", sortable: true },
  { id: 7, KEY: "last_seen", label: "Last Seen", sortable: true },
  { id: 8, KEY: "impression_last_30_days", label: "IMP", sortable: true },
  { id: 9, KEY: "clicks_last_30_days", label: "Clicks", sortable: true },
  { id: 10, KEY: "cost_last_30_days", label: "Cost", sortable: true },
  { id: 11, KEY: "conversions_last_30_days", label: "CVR (%)", sortable: true },
  {
    id: 12,
    KEY: "otherKeywordsCount",
    label: "Other Keywords",
    sortable: true,
  },
  {
    id: 13,
    KEY: "Definition",
    label: "Definition",
    sortable: false,
    type: "custom", // for custom Autocomplete render
  },
];

const definitionOptions = [
  { label: "exclusive", value: "Exclusive" },
  { label: "allowed", value: "Allowed" },
  { label: "negative", value: "Negative" },
];

const STPerformanceUnclassifiedTable = () => {
  const dispatch = useDispatch();
  // const [searchParams, setSearchParams] = useSearchParams();
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "search_term", direction: "desc" });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    stWithPerformanceUnClassified,
    selectedCampaignForUnclassifiedST,
    selectedAdGroupForUnclassifiedST,
    selectedKeywordForUnclassifiedST,
    selectedSearchTermForUnclassifiedST,
    selectedRowsForUnclassifiedST,
    selectedDefinitionsForUnclassifiedST,
  } = useSelector((state: any) => state.searchTermTab);

  //-- Filtering Data
  const filteredRows = useMemo(() => {
    return (sortedData || []).filter((row: any) => {
      const campaignMatch =
        selectedCampaignForUnclassifiedST.length > 0
          ? selectedCampaignForUnclassifiedST.includes(row.campaign_name)
          : true;

      const adgroupMatch =
        selectedAdGroupForUnclassifiedST.length > 0
          ? selectedAdGroupForUnclassifiedST.includes(row.adgroup_name)
          : true;

      const keywordMatch =
        selectedKeywordForUnclassifiedST.length > 0
          ? selectedKeywordForUnclassifiedST.includes(row.keyword_name)
          : true;

      const searchMatch = selectedSearchTermForUnclassifiedST
        ? row.search_term
            ?.toLowerCase()
            .trim()
            .includes(selectedSearchTermForUnclassifiedST?.toLowerCase().trim())
        : true;

      return campaignMatch && adgroupMatch && keywordMatch && searchMatch;
    });
  }, [
    sortedData,
    selectedCampaignForUnclassifiedST,
    selectedAdGroupForUnclassifiedST,
    selectedKeywordForUnclassifiedST,
    selectedSearchTermForUnclassifiedST,
  ]);
  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) return 0;

        let aValue = a[keytoSort];
        let bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  useEffect(() => {
    if (!stWithPerformanceUnClassified) return;
    const modifiedWithUniqueIds = stWithPerformanceUnClassified.map(
      (row: any) => ({
        ...row,
        id: `${uuidv4()}`,
        otherKeywordsCount: 0,
      })
    );
    // OtherKeywordsCount for ALL data BEFORE filtering
    const withKeywordCounts = getUniqueSearchTermsWithDuplicates(
      modifiedWithUniqueIds,
      "otherKeywordsCount",
      "otherKeywordsRows"
    );

    // Then sort the data with accurate counts
    const sortedRows = getSortedData(withKeywordCounts || []);
    // const sortedRows = getSortedData(modifiedWithUniqueIds || []);
    setSortedData(sortedRows);
  }, [stWithPerformanceUnClassified, getSortedData]);

  useEffect(() => {
    if (selectedRowIds.length === 0) return;

    const visibleSelectedIds = selectedRowIds.filter((id) =>
      filteredRows.some((r) => r.id === id)
    );

    if (visibleSelectedIds.length > 0) {
      if (visibleSelectedIds.length !== selectedRowIds.length) {
        setSelectedRowIds(visibleSelectedIds);
        const visibleSelectedRows = filteredRows.filter((r) =>
          visibleSelectedIds.includes(r.id)
        );
        dispatch(setSelectedRowsForUnclassifiedST(visibleSelectedRows));
      }
    } else {
      setSelectedRowIds([]);
      dispatch(setSelectedRowsForUnclassifiedST([]));
    }
    // eslint-disable-next-line
  }, [filteredRows]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  //-- Function to handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = paginatedRows.map((row: any) => row.id);
      setSelectedRowIds(newSelected);
      dispatch(setSelectedRowsForUnclassifiedST(paginatedRows));
      dispatch(setSelectedDefinitionsForUnclassifiedST({})); // clear definitions on select all
    } else {
      setSelectedRowIds([]);
      dispatch(setSelectedRowsForUnclassifiedST([]));
    }
  };

  //-- Function to handle row selection(check on single checkbox)
  const handleSelectRow = (id: any) => {
    const isSelected = selectedRowIds.includes(id);
    let newSelected: any[] = [];
    const updatedDefinitions = { ...selectedDefinitionsForUnclassifiedST };

    if (isSelected) {
      // Deselect row
      newSelected = selectedRowIds.filter((rowId: any) => rowId !== id);
    } else {
      // Select row → clear its definition
      newSelected = [...selectedRowIds, id];
      delete updatedDefinitions[id];
    }

    setSelectedRowIds(newSelected);

    // Update Redux (selected rows)
    const newSelectedRows = filteredRows.filter((row: any) =>
      newSelected.includes(row.id)
    );
    dispatch(setSelectedRowsForUnclassifiedST(newSelectedRows));

    // Update Redux (cleared definitions)
    dispatch(setSelectedDefinitionsForUnclassifiedST(updatedDefinitions));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    // const updatedParams = new URLSearchParams(window.location.search);
    // updatedParams.set("page2", newPage.toString());
    // setSearchParams(updatedParams);
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForUnclassifiedST([]));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForUnclassifiedST([]));
  };

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;

    setSort({
      keytoSort: header.KEY,
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line
  }, [
    selectedCampaignForUnclassifiedST,
    selectedAdGroupForUnclassifiedST,
    selectedKeywordForUnclassifiedST,
    selectedSearchTermForUnclassifiedST,
  ]);

  useEffect(() => {
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForUnclassifiedST([]));
    dispatch(setSelectedDefinitionsForUnclassifiedST({})); // Clear definitions on deselect all
    setPage(0);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedRowsForUnclassifiedST?.length === 0) {
      setSelectedRowIds([]);
      dispatch(setSelectedDefinitionsForUnclassifiedST({})); // Clear definitions on deselect all
    }
    // eslint-disable-next-line
  }, [selectedRowsForUnclassifiedST]);

  return (
    <>
      <STUnclassifiedFilterCard />

      {/* DataGrid */}
      <Stack flexDirection={"column"} alignItems={"flex-end"} gap={2}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            mt: 2,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 5, // a higher zIndex ensures it sits above table rows
                    backgroundColor: "background.paper",
                  }}
                >
                  <Checkbox
                    data-tracker="521"
                    color="secondary"
                    indeterminate={
                      selectedRowIds.length > 0 &&
                      selectedRowIds.length < paginatedRows.length
                    }
                    checked={
                      paginatedRows.length > 0 &&
                      selectedRowIds.length === paginatedRows.length &&
                      selectedRowsForUnclassifiedST.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                {tableHeaders.map((header: any) => {
                  // Which columns should be sticky
                  const isLeftSticky = header.id === 1 || header.id === 2;
                  const isRightSticky =
                    header.id === tableHeaders[tableHeaders.length - 1].id; // last column

                  const leftOffsets: Record<number, number> = {
                    1: 52,
                    2: 162,
                  };

                  const cellSx = isLeftSticky
                    ? ({
                        position: "sticky",
                        left: leftOffsets[header.id] ?? 0,
                        zIndex: 10,
                        backgroundColor: "background.paper",
                        borderRight:
                          header.id === 2 ? "1px solid #e0e0e0" : undefined,
                      } as SxProps<Theme>)
                    : isRightSticky
                    ? ({
                        position: "sticky",
                        right: 0,
                        zIndex: 9,
                        backgroundColor: "background.paper",
                        borderLeft: "1px solid #e0e0e0",
                      } as SxProps<Theme>)
                    : ({} as SxProps<Theme>);

                  return (
                    <TableCell key={header.id} align="left" sx={cellSx}>
                      <TableSortLabel
                        active={sort.keytoSort === header.KEY}
                        direction={
                          sort.keytoSort === header.KEY ? sort.direction : "asc"
                        }
                        onClick={() => handleHeaderClick(header)}
                        disabled={!header.sortable}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((row: any) => {
                  const isSelected = selectedRowIds.includes(row.id);
                  return (
                    <TableRow
                      hover
                      key={row.id}
                      selected={isSelected}
                      // onClick={() => handleSelectRow(row.id)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isSelected
                          ? "rgba(156, 39, 176, 0.08) !important"
                          : "inherit",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? ""
                            : "inherit !important",
                        },
                      }}
                    >
                      <TableCell
                        padding="checkbox"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          backgroundColor: isSelected
                            ? "#f0def3"
                            : "background.paper",
                        }}
                      >
                        <Checkbox
                          data-tracker="538"
                          color="secondary"
                          checked={isSelected}
                          onChange={() => handleSelectRow(row.id)}
                          // onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 52,
                          zIndex: 1,
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          backgroundColor: isSelected
                            ? "#f0def3"
                            : "background.paper",
                        }}
                      >
                        {row.search_term}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 162,
                          zIndex: 1,
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          backgroundColor: isSelected
                            ? "#f0def3"
                            : "background.paper",
                          borderRight: "1px solid #e0e0e0",
                        }}
                      >
                        {row.search_term_match_type ?? ""}
                      </TableCell>
                      <TableCell>{row.keyword_name ?? ""}</TableCell>
                      <TableCell>{row.keyword_matchtype ?? ""}</TableCell>
                      <TableCell>{row.adgroup_name ?? ""}</TableCell>
                      <TableCell>{row.campaign_name ?? ""}</TableCell>

                      <TableCell>
                        {row.last_seen
                          ? dayjs(row.last_seen).format("DD/MM/YYYY")
                          : ""}
                      </TableCell>
                      <TableCell>
                        {row.impression_last_30_days
                          ? formatNumber(row.impression_last_30_days)
                          : ""}
                      </TableCell>
                      <TableCell>
                        {row.clicks_last_30_days
                          ? formatNumber(row.clicks_last_30_days)
                          : ""}
                      </TableCell>
                      <TableCell>
                        {formatTwoDecimal(row.cost_last_30_days, "$")}
                      </TableCell>
                      <TableCell>
                        {formatTwoDecimal(row.conversions_last_30_days, "%")}
                      </TableCell>
                      <TableCell>
                        <Typography
                          data-tracker="543"
                          variant="body2"
                          sx={{
                            fontSize: "0.99rem",
                            fontWeight: 500,
                            color:
                              row.otherKeywordsCount > 0 ? "purple" : "default",
                            cursor:
                              row.otherKeywordsCount > 0
                                ? "pointer"
                                : "default",
                            textDecoration:
                              row.otherKeywordsCount > 0 ? "underline" : "none",
                            "&:hover": {
                              textDecoration:
                                row.otherKeywordsCount > 0
                                  ? "underline"
                                  : "none",
                            },
                          }}
                          onClick={
                            row.otherKeywordsCount > 0
                              ? () => {
                                  dispatch(
                                    setIsOpenOtherKeywordDialogSTOUnclassified(
                                      true
                                    )
                                  );
                                  dispatch(
                                    setOtherKeywordsForUnclassifiedST(
                                      row.otherKeywordsRows || []
                                    )
                                  );
                                  dispatch(
                                    setOtherKeywordRelatedToSearchTerm(
                                      row.search_term
                                    )
                                  );
                                }
                              : undefined
                          }
                        >
                          {row.otherKeywordsCount}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          zIndex: 2,
                          backgroundColor: isSelected
                            ? "#f0def3"
                            : "background.paper",
                          borderLeft: "1px solid #e0e0e0",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          // onClick={(e) => e.stopPropagation()}
                        >
                          <Autocomplete
                            freeSolo={false}
                            data-tracker="542"
                            size="small"
                            disabled={selectedRowIds.includes(row.id)}
                            options={definitionOptions.map((d) => d.value)} // ensure array of strings
                            value={
                              selectedDefinitionsForUnclassifiedST[row.id]
                                ?.action || ""
                            } // get action for this specific row
                            onChange={(_, newValue) => {
                              // Show warning if Exclusive is selected
                              if (newValue === "Exclusive") {
                                dispatch(
                                  setDisplayErrorSnackBar({
                                    message:
                                      "This action will affect all other ad groups within the same campaign scope.",
                                    type: "warning",
                                  })
                                );
                              }
                              // Update the definitions object with this row's data + action
                              const updated = {
                                ...selectedDefinitionsForUnclassifiedST,
                                [row.id]: {
                                  ...row, // spread all row data
                                  action: newValue || null, // add the selected action
                                },
                              };
                              dispatch(
                                setSelectedDefinitionsForUnclassifiedST(updated)
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select from list"
                                variant="standard"
                                size="small"
                                sx={{
                                  fontSize: "12px",
                                  // Show pointer cursor when no value is selected
                                  "& .MuiInputBase-root": {
                                    cursor:
                                      !selectedDefinitionsForUnclassifiedST[
                                        row.id
                                      ]?.action
                                        ? "pointer"
                                        : "default",
                                  },
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                onKeyDown={(e) => {
                                  // Prevent all keyboard input
                                  e.preventDefault();
                                }}
                              />
                            )}
                            // Show clear icon only when there's a selected value
                            clearIcon={
                              selectedDefinitionsForUnclassifiedST[row.id]
                                ?.action
                                ? undefined // Use default clear icon
                                : null // Hide clear icon when no value
                            }
                            sx={{ minWidth: 180 }}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length + 1} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Button
          data-tracker="545"
          variant="contained"
          color="secondary"
          endIcon={<SaveOutlinedIcon />}
          disabled={
            Object.keys(selectedDefinitionsForUnclassifiedST).length === 0 ||
            Object.values(selectedDefinitionsForUnclassifiedST).every(
              (item: any) => !item?.action || item.action.trim() === ""
            )
          }
          onClick={() => {
            dispatch(setIsDefinitionModalOpenForUnclassifiedST(true));
            dispatch(setDefinitionModalSourceForUnclassifiedST("saveButton"));
          }}
        >
          Save changes
        </Button>
      </Stack>

      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 1 }}
      >
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
              {
                display: "none",
              },
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
          }}
        />

        <Pagination
          count={Math.ceil(filteredRows.length / rowsPerPage)}
          onChange={(_, value) => handleChangePage(null, value - 1)}
          page={page + 1}
          showFirstButton
          showLastButton
          color="secondary"
          size="small"
        />

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          classes={{
            displayedRows: "hidden-displayed-rows", // Apply the custom class
          }}
          sx={{
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-input": { marginRight: "15px" },
          }}
        />
      </Stack>
    </>
  );
};

export default STPerformanceUnclassifiedTable;
